/**
 * Theme definitions matching the Flutter app's themes.dart.
 * Each theme has dark and light variants with CSS variable values.
 */

export interface ThemeColors {
  bg: string
  bgSurface: string
  bgCard: string
  text: string
  textMuted: string
  textSecondary: string
  primary: string
  primaryHover: string
  border: string
  userBubble: string
  assistantBubble: string
  systemBubble: string
  barBg: string
  danger: string
  success: string
  bodyBg: string
}

type ThemeVariant = { dark: ThemeColors; light: ThemeColors }

const THEMES: Record<string, ThemeVariant> = {
  sea: {
    dark: {
      bg: '#002233', bgSurface: '#003344', bgCard: '#004455',
      text: '#FAFAFA', textMuted: 'rgba(250,250,250,0.45)', textSecondary: 'rgba(250,250,250,0.7)',
      primary: '#039BE5', primaryHover: '#4FC3F7', border: 'rgba(250,250,250,0.1)',
      userBubble: 'rgba(250,250,250,0.08)', assistantBubble: 'rgba(1,87,155,0.15)',
      systemBubble: 'rgba(3,155,229,0.1)', barBg: '#006064',
      danger: '#ef4444', success: '#44cc44', bodyBg: '#001119',
    },
    light: {
      bg: '#DAE8EE', bgSurface: '#FAFAFA', bgCard: '#FFFFFF',
      text: '#1a1a1a', textMuted: 'rgba(0,0,0,0.45)', textSecondary: 'rgba(0,0,0,0.7)',
      primary: '#0277BD', primaryHover: '#4FC3F7', border: 'rgba(0,0,0,0.12)',
      userBubble: 'rgba(0,0,0,0.04)', assistantBubble: 'rgba(2,119,189,0.08)',
      systemBubble: 'rgba(2,119,189,0.06)', barBg: '#006064',
      danger: '#d32f2f', success: '#2e7d32', bodyBg: '#C8DDE6',
    },
  },
  winter: {
    dark: {
      bg: '#121212', bgSurface: '#1E1E1E', bgCard: '#2A2A2A',
      text: '#FAFAFA', textMuted: 'rgba(250,250,250,0.45)', textSecondary: 'rgba(250,250,250,0.7)',
      primary: '#2196F3', primaryHover: '#64B5F6', border: 'rgba(250,250,250,0.1)',
      userBubble: 'rgba(250,250,250,0.06)', assistantBubble: 'rgba(33,150,243,0.12)',
      systemBubble: 'rgba(33,150,243,0.08)', barBg: '#0D47A1',
      danger: '#ef4444', success: '#44cc44', bodyBg: '#0A0A0A',
    },
    light: {
      bg: '#F5F5F5', bgSurface: '#FFFFFF', bgCard: '#FFFFFF',
      text: '#1a1a1a', textMuted: 'rgba(0,0,0,0.45)', textSecondary: 'rgba(0,0,0,0.7)',
      primary: '#2196F3', primaryHover: '#1976D2', border: 'rgba(0,0,0,0.12)',
      userBubble: 'rgba(0,0,0,0.04)', assistantBubble: 'rgba(33,150,243,0.08)',
      systemBubble: 'rgba(33,150,243,0.06)', barBg: '#2196F3',
      danger: '#d32f2f', success: '#2e7d32', bodyBg: '#E8E8E8',
    },
  },
  greyscale: {
    dark: {
      bg: '#0A0A0A', bgSurface: '#1C1C1C', bgCard: '#282828',
      text: '#FAFAFA', textMuted: 'rgba(250,250,250,0.45)', textSecondary: 'rgba(250,250,250,0.7)',
      primary: '#9E9E9E', primaryHover: '#BDBDBD', border: 'rgba(250,250,250,0.1)',
      userBubble: 'rgba(250,250,250,0.06)', assistantBubble: 'rgba(158,158,158,0.12)',
      systemBubble: 'rgba(158,158,158,0.08)', barBg: '#212121',
      danger: '#ef4444', success: '#44cc44', bodyBg: '#050505',
    },
    light: {
      bg: '#F5F5F5', bgSurface: '#FFFFFF', bgCard: '#FFFFFF',
      text: '#1a1a1a', textMuted: 'rgba(0,0,0,0.45)', textSecondary: 'rgba(0,0,0,0.7)',
      primary: '#424242', primaryHover: '#616161', border: 'rgba(0,0,0,0.12)',
      userBubble: 'rgba(0,0,0,0.04)', assistantBubble: 'rgba(66,66,66,0.08)',
      systemBubble: 'rgba(66,66,66,0.06)', barBg: '#424242',
      danger: '#d32f2f', success: '#2e7d32', bodyBg: '#E8E8E8',
    },
  },
  forest: {
    dark: {
      bg: '#0A1F0A', bgSurface: '#1B2E1B', bgCard: '#243824',
      text: '#FAFAFA', textMuted: 'rgba(250,250,250,0.45)', textSecondary: 'rgba(250,250,250,0.7)',
      primary: '#66BB6A', primaryHover: '#81C784', border: 'rgba(250,250,250,0.1)',
      userBubble: 'rgba(250,250,250,0.06)', assistantBubble: 'rgba(56,142,60,0.15)',
      systemBubble: 'rgba(102,187,106,0.1)', barBg: '#1B5E20',
      danger: '#ef4444', success: '#44cc44', bodyBg: '#061206',
    },
    light: {
      bg: '#F1F8E9', bgSurface: '#FFFFFF', bgCard: '#FFFFFF',
      text: '#1a1a1a', textMuted: 'rgba(0,0,0,0.45)', textSecondary: 'rgba(0,0,0,0.7)',
      primary: '#2E7D32', primaryHover: '#388E3C', border: 'rgba(0,0,0,0.12)',
      userBubble: 'rgba(0,0,0,0.04)', assistantBubble: 'rgba(46,125,50,0.08)',
      systemBubble: 'rgba(46,125,50,0.06)', barBg: '#2E7D32',
      danger: '#d32f2f', success: '#2e7d32', bodyBg: '#E0EED6',
    },
  },
  desert: {
    dark: {
      bg: '#2D1E0F', bgSurface: '#3D2B1F', bgCard: '#4D3828',
      text: '#FAFAFA', textMuted: 'rgba(250,250,250,0.45)', textSecondary: 'rgba(250,250,250,0.7)',
      primary: '#E6A65D', primaryHover: '#FFB74D', border: 'rgba(250,250,250,0.1)',
      userBubble: 'rgba(250,250,250,0.06)', assistantBubble: 'rgba(179,90,31,0.15)',
      systemBubble: 'rgba(230,166,93,0.1)', barBg: '#8B4513',
      danger: '#ef4444', success: '#44cc44', bodyBg: '#1F1408',
    },
    light: {
      bg: '#FFF8E1', bgSurface: '#FFFFFF', bgCard: '#FFFFFF',
      text: '#1a1a1a', textMuted: 'rgba(0,0,0,0.45)', textSecondary: 'rgba(0,0,0,0.7)',
      primary: '#D2691E', primaryHover: '#E6A65D', border: 'rgba(0,0,0,0.12)',
      userBubble: 'rgba(0,0,0,0.04)', assistantBubble: 'rgba(210,105,30,0.08)',
      systemBubble: 'rgba(210,105,30,0.06)', barBg: '#D2691E',
      danger: '#d32f2f', success: '#2e7d32', bodyBg: '#F0E6CE',
    },
  },
  rose: {
    dark: {
      bg: '#2C0A1A', bgSurface: '#3C1A2B', bgCard: '#4C2A3B',
      text: '#FAFAFA', textMuted: 'rgba(250,250,250,0.45)', textSecondary: 'rgba(250,250,250,0.7)',
      primary: '#EC407A', primaryHover: '#F48FB1', border: 'rgba(250,250,250,0.1)',
      userBubble: 'rgba(250,250,250,0.06)', assistantBubble: 'rgba(194,24,91,0.15)',
      systemBubble: 'rgba(236,64,122,0.1)', barBg: '#880E4F',
      danger: '#ef4444', success: '#44cc44', bodyBg: '#1E0610',
    },
    light: {
      bg: '#FCE4EC', bgSurface: '#FFFFFF', bgCard: '#FFFFFF',
      text: '#1a1a1a', textMuted: 'rgba(0,0,0,0.45)', textSecondary: 'rgba(0,0,0,0.7)',
      primary: '#E91E63', primaryHover: '#F48FB1', border: 'rgba(0,0,0,0.12)',
      userBubble: 'rgba(0,0,0,0.04)', assistantBubble: 'rgba(233,30,99,0.08)',
      systemBubble: 'rgba(233,30,99,0.06)', barBg: '#E91E63',
      danger: '#d32f2f', success: '#2e7d32', bodyBg: '#F0CCD6',
    },
  },
  mocha: {
    dark: {
      bg: '#2A1F1A', bgSurface: '#3D2F28', bgCard: '#4D3F38',
      text: '#FAFAFA', textMuted: 'rgba(250,250,250,0.45)', textSecondary: 'rgba(250,250,250,0.7)',
      primary: '#9B7461', primaryHover: '#D4A59A', border: 'rgba(250,250,250,0.1)',
      userBubble: 'rgba(250,250,250,0.06)', assistantBubble: 'rgba(107,78,61,0.2)',
      systemBubble: 'rgba(155,116,97,0.1)', barBg: '#4A2C2A',
      danger: '#ef4444', success: '#44cc44', bodyBg: '#1C1410',
    },
    light: {
      bg: '#F5EDE0', bgSurface: '#FFFFFF', bgCard: '#FFFFFF',
      text: '#1a1a1a', textMuted: 'rgba(0,0,0,0.45)', textSecondary: 'rgba(0,0,0,0.7)',
      primary: '#A47864', primaryHover: '#D4A59A', border: 'rgba(0,0,0,0.12)',
      userBubble: 'rgba(0,0,0,0.04)', assistantBubble: 'rgba(164,120,100,0.08)',
      systemBubble: 'rgba(164,120,100,0.06)', barBg: '#8B5E3C',
      danger: '#d32f2f', success: '#2e7d32', bodyBg: '#E6DCCF',
    },
  },
  rainbow: {
    dark: {
      bg: '#1A1A2E', bgSurface: '#292942', bgCard: '#363652',
      text: '#FAFAFA', textMuted: 'rgba(250,250,250,0.45)', textSecondary: 'rgba(250,250,250,0.7)',
      primary: '#4ECDC4', primaryHover: '#FF6B6B', border: 'rgba(250,250,250,0.1)',
      userBubble: 'rgba(250,250,250,0.06)', assistantBubble: 'rgba(78,205,196,0.12)',
      systemBubble: 'rgba(255,107,107,0.1)', barBg: '#4ECDC4',
      danger: '#FF6B6B', success: '#6BCF7F', bodyBg: '#10101E',
    },
    light: {
      bg: '#FFF4E0', bgSurface: '#FFFFFF', bgCard: '#FFFFFF',
      text: '#1a1a1a', textMuted: 'rgba(0,0,0,0.45)', textSecondary: 'rgba(0,0,0,0.7)',
      primary: '#FF6B6B', primaryHover: '#4ECDC4', border: 'rgba(0,0,0,0.12)',
      userBubble: 'rgba(0,0,0,0.04)', assistantBubble: 'rgba(255,107,107,0.08)',
      systemBubble: 'rgba(78,205,196,0.06)', barBg: '#4ECDC4',
      danger: '#d32f2f', success: '#2e7d32', bodyBg: '#F0E4CE',
    },
  },
}

/** Apply a theme to the document by setting CSS variables on :root */
export function applyTheme(themeName: string, darkMode: boolean): void {
  const variant = THEMES[themeName]
  if (!variant) return
  const colors = darkMode ? variant.dark : variant.light
  const root = document.documentElement.style

  root.setProperty('--bg', colors.bg)
  root.setProperty('--bg-surface', colors.bgSurface)
  root.setProperty('--bg-card', colors.bgCard)
  root.setProperty('--text', colors.text)
  root.setProperty('--text-muted', colors.textMuted)
  root.setProperty('--text-secondary', colors.textSecondary)
  root.setProperty('--primary', colors.primary)
  root.setProperty('--primary-hover', colors.primaryHover)
  root.setProperty('--border', colors.border)
  root.setProperty('--user-bubble', colors.userBubble)
  root.setProperty('--assistant-bubble', colors.assistantBubble)
  root.setProperty('--system-bubble', colors.systemBubble)
  root.setProperty('--bar-bg', colors.barBg)
  root.setProperty('--danger', colors.danger)
  root.setProperty('--success', colors.success)

  document.body.style.background = colors.bodyBg
}

export function getThemeNames(): string[] {
  return Object.keys(THEMES)
}

/** Get the primary accent color for a theme (for settings preview dots) */
export function getThemeAccent(themeName: string): string {
  const variant = THEMES[themeName]
  return variant?.dark.primary || '#888'
}
